import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@performyard/py-components";
import type { CreateCustomSideNavThemeRequest, UpdateCustomSideNavThemeRequest } from "@performyard/service-contracts";

import { sideNavThemesQuery } from "../../queries";
import {
	createCustomSideNavTheme,
	deleteCustomSideNavTheme,
	selectSideNavTheme,
	updateCustomSideNavTheme,
} from "../../services/defs/internal-api";

const NAVIGATION_CONFIG_KEY = ["navigation-config"] as const;

const invalidateThemeQueries = async (queryClient: ReturnType<typeof useQueryClient>, companyId?: string) => {
	// Source the side-nav-themes key from the query factory so it can't drift
	// from the query definition.
	await Promise.all([
		queryClient.invalidateQueries({ queryKey: sideNavThemesQuery().queryKey }),
		queryClient.invalidateQueries({ queryKey: sideNavThemesQuery(companyId).queryKey }),
		queryClient.invalidateQueries({ queryKey: NAVIGATION_CONFIG_KEY }),
	]);
};

export const useSideNavThemes = (companyId?: string) => {
	const query = useQuery(sideNavThemesQuery(companyId));
	return {
		data: query.data,
		isLoading: query.isLoading,
		error: query.error,
		refetch: query.refetch,
	};
};

export const useCreateSideNavTheme = (companyId?: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateCustomSideNavThemeRequest) => createCustomSideNavTheme(data, companyId),
		onSuccess: async () => {
			await invalidateThemeQueries(queryClient, companyId);
		},
		onError: (error) => {
			console.error("Error creating custom side-nav theme:", error);
			toast.error("Failed to add custom theme");
		},
	});
};

export const useUpdateSideNavTheme = (companyId?: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ themeId, data }: { themeId: string; data: UpdateCustomSideNavThemeRequest }) =>
			updateCustomSideNavTheme(themeId, data, companyId),
		onSuccess: async () => {
			await invalidateThemeQueries(queryClient, companyId);
		},
		onError: (error) => {
			console.error("Error updating custom side-nav theme:", error);
			toast.error("Failed to update custom theme");
		},
	});
};

export const useDeleteSideNavTheme = (companyId?: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (themeId: string) => deleteCustomSideNavTheme(themeId, companyId),
		onSuccess: async () => {
			await invalidateThemeQueries(queryClient, companyId);
		},
		onError: (error) => {
			console.error("Error deleting custom side-nav theme:", error);
			toast.error("Failed to delete custom theme");
		},
	});
};

export const useSelectSideNavTheme = (companyId?: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (themeId: string) => selectSideNavTheme(themeId, companyId),
		onSuccess: async () => {
			await invalidateThemeQueries(queryClient, companyId);
			toast.success("Theme saved");
		},
		onError: (error) => {
			console.error("Error selecting side-nav theme:", error);
			toast.error("Failed to save theme.");
		},
	});
};
